import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators} from "@angular/forms";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {debounceTime} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {IntFilterComponent} from "./int-filter/int-filter.component";
import {DateFilterComponent} from "./date-filter/date-filter.component";
import {AuthenticatedBaseComponent} from '../../authenticated/authenticated-base.component';
import {TableDisplayConfig} from '../../entities/table-display-config.entity';
import {TitleService} from '../../services/title.service';
import {ContentHeaderService} from '../../services/content-header.service';
import {JsonPipe, NgForOf, NgIf} from '@angular/common';
import {environment} from '../../../environments/environment';
import {
  MultiSelectWithStaticOptionsStaticComponent
} from '../form-inputs/multi-select-static-with-static-options/multi-select-with-static-options-static.component';
import {Backend} from '@generated-backend-types';
import {MultiSelectEnumComponent} from '../form-inputs/multi-select-enum/multi-select-enum.component';
import {FormGroupComponent} from '../form-inputs/form-group/form-group.component';
import {
  MultiSelectWithServiceIdArrayComponent
} from '../form-inputs/multi-select-with-service-id-array/multi-select-with-service-id-array.component';
import {EmailService} from '../../services/api/administration/email.service';
import {DiagnosisService} from '../../services/api/diagnosis.service';
import {UserService} from '../../services/api/administration/user.service';
import {AddableTeamLeadersService} from '../../services/api/time-tracking/addable-team-leaders.service';
import {ProcessService} from '../../services/api/quality-management/process.service';
import {TimeTrackingEmployeeService} from '../../services/api/time-tracking/time-tracking-employee.service';
import TableConfigColumnTypeEnum = Backend.App.Enums.TableConfigColumnTypeEnum;
import TableConfig = Backend.App.Resources.Dashboard.Meta.TableConfig;
import TableConfigColumn = Backend.App.Resources.Dashboard.Meta.TableConfigColumn;
import ModelStateEnum = Backend.App.Enums.ModelStateEnum;
import PatientStateEnum = Backend.App.Enums.PatientStateEnum;
import EmailTypeEnum = Backend.App.Enums.Administration.EmailTypeEnum;
import PatientLanguageEnum = Backend.App.Enums.PatientLanguageEnum;
import PatientGenderEnum = Backend.App.Enums.PatientGenderEnum;
import InquiryStateEnum = Backend.App.Enums.InquiryStateEnum;
import ProcessCategoryEnum = Backend.App.Enums.QualityManagement.ProcessCategoryEnum;
import ProcessRoleEnum = Backend.App.Enums.QualityManagement.ProcessRoleEnum;
import ProcessPriorityEnum = Backend.App.Enums.QualityManagement.ProcessPriorityEnum;
import ProcessComplexityEnum = Backend.App.Enums.QualityManagement.ProcessComplexityEnum;
import {PatientService} from '../../services/api/patients/patient.service';
import {AddableDoctorService} from '../../services/api/patients/addable-doctor.service';
import {AddableBackofficeUserService} from '../../services/api/patients/addable-backoffice-user.service';
import TimeTrackingEntryTypeEnum = Backend.App.Enums.TimeTracking.TimeTrackingEntryTypeEnum;
import TimeTrackingAbsenceRequestStateEnum = Backend.App.Enums.TimeTracking.TimeTrackingAbsenceRequestStateEnum;
import GermanStateEnum = Backend.App.Enums.TimeTracking.GermanStateEnum;

@Component({
  selector: 'app-table-filter',
  templateUrl: './table-filter.component.html',
  imports: [
    ReactiveFormsModule,
    IntFilterComponent,
    DateFilterComponent,
    NgIf,
    NgForOf,
    TranslatePipe,
    JsonPipe,
    MultiSelectWithStaticOptionsStaticComponent,
    MultiSelectEnumComponent,
    FormGroupComponent,
    MultiSelectWithServiceIdArrayComponent,
  ],
  standalone: true
})
export class TableFilterComponent extends AuthenticatedBaseComponent implements OnInit {

  public readonly TableConfigColumnTypeEnum = TableConfigColumnTypeEnum;

  @Output() public settingsClicked: EventEmitter<void> = new EventEmitter<void>();

  @Output() public tableDisplayConfigChange: EventEmitter<TableDisplayConfig> = new EventEmitter<TableDisplayConfig>();
  public tableConfig?: TableConfig;
  @Input() public globalFilter?: Record<string, string>;
  @Input() public quickFilterOnly = false;
  @Input() public quickFilterSpacing = false;
  @Input() public rowSpacing = true;
  @Input() public compactView = false;
  @Input() public smallInput = true;
  @Input() public inlineInput = true;
  @Input() public withoutLabel = false;
  public columnFilterForm = this.fb.group({});
  protected readonly Validators = Validators;
  protected readonly environment = environment;
  protected readonly ModelStateEnum = ModelStateEnum;
  protected readonly PatientStateEnum = PatientStateEnum;
  protected readonly EmailTypeEnum = EmailTypeEnum;
  protected readonly PatientLanguageEnum = PatientLanguageEnum;
  protected readonly PatientGenderEnum = PatientGenderEnum;
  protected readonly InquiryStateEnum = InquiryStateEnum;
  protected readonly ProcessCategoryEnum = ProcessCategoryEnum;
  protected readonly ProcessRoleEnum = ProcessRoleEnum;
  protected readonly ProcessPriorityEnum = ProcessPriorityEnum;
  protected readonly ProcessComplexityEnum = ProcessComplexityEnum;
  protected readonly TimeTrackingEntryTypeEnum = TimeTrackingEntryTypeEnum;
  protected readonly TimeTrackingAbsenceRequestStateEnum = TimeTrackingAbsenceRequestStateEnum;
  protected readonly GermanStateEnum = GermanStateEnum;

  public constructor(
    protected override cdr: ChangeDetectorRef,
    protected override translate: TranslateService,
    protected override contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly fb: NonNullableFormBuilder,
    private toastrService: ToastrService,
    public readonly emailService: EmailService,
    public readonly diagnosisService: DiagnosisService,
    public readonly patientService: PatientService,
    public readonly userService: UserService,
    public readonly addableDoctorService: AddableDoctorService,
    public readonly addableBackofficeUserService: AddableBackofficeUserService,
    public readonly addableTeamLeadersService: AddableTeamLeadersService,
    public readonly processService: ProcessService,
    public readonly timeTrackingEmployeeService: TimeTrackingEmployeeService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public _tableDisplayConfig?: TableDisplayConfig;

  @Input()
  public set tableDisplayConfig(conf: TableDisplayConfig | undefined) {
    if (!conf) {
      return;
    }
    this.tableConfig = conf.tableConfig;
    this._tableDisplayConfig = JSON.parse(JSON.stringify(conf));

    const values: { [key: string]: string | string[] | null } = {}

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this._tableDisplayConfig.tableConfig?.columns?.map(col => {
      if (col.filterable) {
        let value: string | string[] | null
        if (this._tableDisplayConfig && this._tableDisplayConfig.filter[col.name] || conf.filter[col.name] === '') {
          value = conf.filter[col.name];
        } else {
          value = col.default_filter;
        }
        if (typeof value === 'string' && value?.includes(',')) {
          value = value.split(',');
        }

        values[col.name] = value;

        if (!this.columnFilterForm.get(col.name)) {
          this.columnFilterForm.addControl(col.name, this.getFormControlFor(col), {emitEvent: false});
        }
      }
    })

    this.columnFilterForm.reset(values, {emitEvent: true, onlySelf: false})
    this.columnFilterForm.markAsUntouched();
  }

  public resetFilter() {
    this.columnFilterForm.reset();
    this.tableConfig?.columns.forEach((col: TableConfigColumn) => {
      this.columnFilterForm.setControl(col.name, this.getFormControlFor(col));
    })
  }

  public ngOnInit() {

    this.columnFilterForm.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(
      (formValue: Record<string, string[] | string | boolean | undefined | null>) => {

        const oldFilter = JSON.parse(JSON.stringify(this._tableDisplayConfig));

        if (this._tableDisplayConfig) {
          this._tableDisplayConfig.filter = {};
          Object.keys(formValue).forEach((key) => {
            const value = formValue[key];
            if (Array.isArray(value) && this._tableDisplayConfig) {
              if (value.length > 0) {
                this._tableDisplayConfig.filter[key] = value.join(",")
              } else {
                delete this._tableDisplayConfig.filter[key];
              }
            } else if (typeof value === "string" && this._tableDisplayConfig) {
              if (value !== '') {
                this._tableDisplayConfig.filter[key] = value;
              } else {
                delete this._tableDisplayConfig.filter[key];
              }

            } else if (value === null && this._tableDisplayConfig) {

              delete this._tableDisplayConfig.filter[key];
            } else {
              console.error({key, value})
              throw Error('Unhandled Datatype in filter')
            }

          });

          if (!this.areObjectsIdentical(oldFilter, this._tableDisplayConfig) && this.columnFilterForm.valid) {
            this.tableDisplayConfigChange.emit(this._tableDisplayConfig)
          }
          // else {
          //   this.columnFilterForm.markAllAsTouched();
          // }

        }

      }
    )
  }

  private getFormControlFor(col: TableConfigColumn): FormControl {
    const formControl: FormControl = new FormControl(col.default_filter);

    if (col.required_filter) {
      switch (col.type) {
        case TableConfigColumnTypeEnum.BOOL:
        case TableConfigColumnTypeEnum.ARRAY:
        case TableConfigColumnTypeEnum.STRING:
          formControl.setValidators(col.required_filter ? [Validators.required] : []);
          break;
        default:
          console.error('required filter for column type ' + col.type + ' not implemented');
          this.toastrService.error('required filter for column type ' + col.type + ' not implemented');
      }
    }

    formControl.valueChanges.subscribe(() => formControl.markAsTouched());

    return formControl;
  }

  private areObjectsIdentical(obj1: { [key: string]: unknown }, obj2: { [key: string]: unknown }): boolean {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // Check if the number of keys is the same
    if (keys1.length !== keys2.length) {
      return false;
    }

    // Check if all keys in obj1 have the same values in obj2
    for (const key of keys1) {
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }

    return true;
  }
}
